import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConnectionState, useRoomInfo } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";

/** `h:mm:ss` once the hour is passed, `mm:ss` before it. */
function formatted(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    const padded = `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
    return hours > 0 ? `${hours}:${padded}` : padded;
}

/**
 * Seconds since this room connected, or null before it did.
 *
 * The start is kept in `sessionStorage` under the room's name so a reload — the
 * first thing anyone does when video misbehaves — resumes the count instead of
 * quietly restarting it at zero, which would understate the session by however
 * long it had already run. Two rooms in two tabs are two sessions, and the key
 * being the room's name is what keeps their clocks apart.
 */
function useElapsed(room: string | null | undefined, running: boolean): number | null {
    const [startedAt, setStartedAt] = useState<number | null>(null);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!running || !room) return;
        const key = `alchely-meeting-started:${room}`;
        let stored = Number(window.sessionStorage.getItem(key));
        if (!stored) {
            stored = Date.now();
            try {
                window.sessionStorage.setItem(key, String(stored));
            } catch {
                // Private browsing can refuse storage; the count is still right
                // for as long as the page stays open.
            }
        }
        setStartedAt(stored);
    }, [running, room]);

    useEffect(() => {
        if (startedAt === null) return;
        const tick = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(tick);
    }, [startedAt]);

    if (startedAt === null) return null;
    return Math.max(0, Math.floor((now - startedAt) / 1000));
}

/**
 * How long the session has been running.
 *
 * Sessions are sold in fifty-minute hours, and a therapist watching a clock on
 * the wall is a therapist not watching their patient. Counting from the moment
 * the room connects rather than from when the page opened, because the wait for
 * a patient to arrive is not part of the session.
 *
 * It reads as context rather than as a control: it used to sit in the middle of
 * the control bar, between the microphone and the whiteboard, which made the
 * one thing on screen you cannot press look like the things you can.
 */
export function MeetingElapsed({
    room,
    running,
}: {
    /** The room whose clock this is. Its name is the storage key. */
    room: string | null | undefined;
    /** Whether the call is up. Nothing is counted before it is. */
    running: boolean;
}) {
    const { t } = useTranslation();
    const elapsed = useElapsed(room, running);

    // Nothing to say before the call is up: a timer at 00:00 next to a room
    // that has not connected reads as a stopped clock.
    if (elapsed === null) return null;

    return (
        <span
            role="timer"
            aria-label={t("meetingControls.elapsed")}
            title={t("meetingControls.elapsed")}
            className="rounded-lg border border-room-line bg-room-raised px-2.5 py-1 font-mono text-xs tabular-nums text-room-ink"
        >
            {formatted(elapsed)}
        </span>
    );
}

/**
 * The same clock, reading the room from the LiveKit context it is inside.
 *
 * The therapist's session page keeps its header outside `LiveKitRoom` — the
 * title and the share link exist before a token does — so that page uses
 * `MeetingElapsed` directly and tells it which room it is timing.
 */
export default function MeetingTimer() {
    const { name: room } = useRoomInfo();
    const state = useConnectionState();

    return <MeetingElapsed room={room} running={state === ConnectionState.Connected} />;
}
